import React, { useEffect, useState, useRef } from 'react';
import { useStateSelector } from '../../store/selectors';
import { SynchronizationConfirmationDialog } from './SynchronizationConfirmationDialog';
import createNotification from '../../utils/createNotification';
import { areValuesEqual } from '../../utils/typedHelper';

export enum SyncField {
    FteDvm = 0,
    LtmRevenue = 1,
}

export interface DataSynchronizationValidatorProps {
    fteDvm: number;
    ltmRevenue: number;
    practiceId?: number;
    sync?: number | string;
    onSync?: () => void;
    fieldsToSync?: SyncField[];
}

export const DataSynchronizationValidator = (
    props: DataSynchronizationValidatorProps
) => {
    const fieldsToSync = props.fieldsToSync || [SyncField.FteDvm, SyncField.LtmRevenue];
    const shouldSyncFteDvm = fieldsToSync.includes(SyncField.FteDvm);
    const shouldSyncLtmRevenue = fieldsToSync.includes(SyncField.LtmRevenue);

    const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
    const [previousFteDvm, setPreviousFteDvm] = useState<number | null>(null);
    const [previousLtmRevenue, setPreviousLtmRevenue] = useState<number | null>(null);
    const [isSyncing, setIsSyncing] = useState<boolean>(false);
    const [isLoadingPreviousValues, setIsLoadingPreviousValues] = useState<boolean>(false);
    const [pendingFteDvm, setPendingFteDvm] = useState<number>(props.fteDvm);
    const [pendingLtmRevenue, setPendingLtmRevenue] = useState<number>(
        props.ltmRevenue
    );
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const previousSyncRef = useRef<number | string | undefined>(props.sync);
    const previousPracticeIdRef = useRef<number | undefined>(props.practiceId);
    const axios = useStateSelector((state) => state.core.axios);

    const fetchPreviousValues = async (practiceId: number) => {
        if (!practiceId) {
            return;
        }

        setIsLoadingPreviousValues(true);
        try {
            const response = await axios.get(`/api/Practice/GetPracticeSyncData?practiceId=${practiceId}`);
            if (response.status === 200 && response.data) {
                setPreviousFteDvm(response.data.fteDvm ?? null);
                setPreviousLtmRevenue(response.data.ltmRevenue ?? null);
                setIsInitialized(true);
            } else {
                setPreviousFteDvm(null);
                setPreviousLtmRevenue(null);
                setIsInitialized(true);
            }
        } catch (error) {
            console.error('Error fetching previous practice values:', error);
            setPreviousFteDvm(null);
            setPreviousLtmRevenue(null);
            setIsInitialized(true);
        } finally {
            setIsLoadingPreviousValues(false);
        }
    };

    useEffect(() => {
        if (props.practiceId && props.practiceId !== previousPracticeIdRef.current) {
            previousPracticeIdRef.current = props.practiceId;
            setIsInitialized(false);
            fetchPreviousValues(props.practiceId);
        } else if (props.practiceId && !isInitialized) {
            fetchPreviousValues(props.practiceId);
        }
    }, [props.practiceId]);

    useEffect(() => {
        if (
            !isInitialized ||
            isLoadingPreviousValues ||
            props.sync === undefined ||
            props.sync === previousSyncRef.current
        ) {
            return;
        }

        previousSyncRef.current = props.sync;

        const hasFteDvmChange = shouldSyncFteDvm && (previousFteDvm === null || !areValuesEqual(previousFteDvm, props.fteDvm));
        const hasLtmRevenueChange = shouldSyncLtmRevenue && (previousLtmRevenue === null || !areValuesEqual(previousLtmRevenue, props.ltmRevenue));

        if (hasFteDvmChange || hasLtmRevenueChange) {
            setPendingFteDvm(props.fteDvm);
            setPendingLtmRevenue(props.ltmRevenue);
            setIsDialogVisible(true);
        } else {
            props.onSync?.();
        }
    }, [
        props.sync,
        props.fteDvm,
        props.ltmRevenue,
        previousFteDvm,
        previousLtmRevenue,
        isInitialized,
        isLoadingPreviousValues,
        shouldSyncFteDvm,
        shouldSyncLtmRevenue,
    ]);

    const performSync = () => {
        const hasFteDvmChange = shouldSyncFteDvm && (previousFteDvm === null || !areValuesEqual(previousFteDvm, props.fteDvm));
        const hasLtmRevenueChange = shouldSyncLtmRevenue && (previousLtmRevenue === null || !areValuesEqual(previousLtmRevenue, props.ltmRevenue));

        setIsSyncing(true);
        axios
            .post('/api/Practice/SynchronizeData', {
                practiceId: props.practiceId,
                fteDvm: hasFteDvmChange ? props.fteDvm : null,
                ltmRevenue: hasLtmRevenueChange ? props.ltmRevenue : null,
            })
            .then((response) => {
                if (response.status === 200) {
                    if (shouldSyncFteDvm) {
                        setPreviousFteDvm(props.fteDvm);
                    }
                    if (shouldSyncLtmRevenue) {
                        setPreviousLtmRevenue(props.ltmRevenue);
                    }
                    setIsDialogVisible(false);
                    props.onSync?.();
                } else {
                    createNotification(
                        'Something went wrong while synchronizing data',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Something went wrong while synchronizing data',
                    'error'
                );
            })
            .finally(() => {
                setIsSyncing(false);
            });
    };

    const handleConfirmSync = () => {
        performSync();
    };

    const handleCancelSync = () => {
        setIsDialogVisible(false);
        props.onSync?.();
    };

    return (
        <>
            {isDialogVisible && (
                <SynchronizationConfirmationDialog
                    isVisible={isDialogVisible}
                    setIsVisible={setIsDialogVisible}
                    previousFteDvm={shouldSyncFteDvm ? (previousFteDvm ?? 0) : undefined}
                    previousLtmRevenue={shouldSyncLtmRevenue ? (previousLtmRevenue ?? 0) : undefined}
                    newFteDvm={shouldSyncFteDvm ? pendingFteDvm : undefined}
                    newLtmRevenue={shouldSyncLtmRevenue ? pendingLtmRevenue : undefined}
                    onConfirm={handleConfirmSync}
                    onCancel={handleCancelSync}
                    isLoading={isSyncing}
                />
            )}
        </>
    );
};
